import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import { requestsActions } from '../../../actions';
import { getValidationSchemaForFields } from '../../../util';
import { FormField } from '../../common/Forms';
import { renderErrors } from '../../common/Forms/utils';

function useMainDataForRequest(requestNum, requests) {
    return useMemo(() => {
        let request;
        if (!requests) return request;
        request = [...requests.author, ...requests.participant].find((req) => +requestNum === +req.num);
        if (request) {
            const { nomination } = request;
            const dataSchema = getValidationSchemaForFields(nomination.extDataFields);
            const initialValues = nomination.extDataFields.reduce(
                (obj, field) => ({
                    ...obj,
                    [field.id]: (request.extData[field.id] && request.extData[field.id].value) || '',
                }),
                {}
            );
            return [request, nomination, dataSchema, initialValues];
        }
        return [null, null, null];
    }, [requestNum, requests]);
}

function ExtDataPage(props) {
    const { requests, errors, getting, clearErrors, saveExtData, user } = props;
    const { requestNum } = useParams();
    const [request, nomination, schema, initialValues] = useMainDataForRequest(requestNum, requests);
    const readOnly = useMemo(() => {
        return (
            !user ||
            !request ||
            user.id !== request.author.id ||
            dayjs().isAfter(dayjs(request.nomination.changeStopDate).add(10, 'day'))
        );
    }, [user, request]);

    useEffect(() => {
        return () => clearErrors();
    }, [clearErrors]);

    const handleSubmit = useCallback(
        (values) => {
            const transformedValues = schema.cast(values);
            const extData = {
                requestId: request.id,
                fieldsValue: transformedValues,
            };
            saveExtData(extData);
        },
        [saveExtData, request, schema]
    );

    const formik = useFormik({
        initialValues,
        validate: schema && schema.validate,
        onSubmit: handleSubmit,
    });

    const handleChange = useCallback(
        (value, name) => {
            if (errors && errors[name]) clearErrors();
        },
        [errors, clearErrors]
    );

    const submitting = formik.isValidating || getting;

    return (
        <Container className="my-3 my-sm-5">
            {request ? (
                <Card className="mx-auto">
                    <Card.Header>
                        <Card.Title className="mb-0">Доп данные для заявки №{request.num}</Card.Title>
                        <div className="small">{request.title}</div>
                    </Card.Header>
                    <Card.Body>
                        <Form onSubmit={formik.handleSubmit} noValidate>
                            {nomination &&
                                nomination.extDataFields.map((field) => (
                                    <Form.Group key={field.id}>
                                        <FormField
                                            field={field}
                                            formik={formik}
                                            errors={errors}
                                            onChange={handleChange}
                                            readOnly={readOnly}
                                            disabled={readOnly}
                                        />
                                    </Form.Group>
                                ))}
                            {errors && errors.globalError && (
                                <Alert variant="danger">{renderErrors(errors.globalError)}</Alert>
                            )}

                            {!readOnly && (
                                <Form.Group className="text-center">
                                    <Button
                                        variant="outline-primary"
                                        type="submit"
                                        className="px-5"
                                        disabled={submitting}
                                    >
                                        {submitting && (
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                        )}
                                        <span>Сохранить</span>
                                    </Button>
                                </Form.Group>
                            )}
                        </Form>
                    </Card.Body>
                </Card>
            ) : (
                <h2 className="text-center">Нет такой заявки</h2>
            )}
        </Container>
    );
}

function mapStateToProps(state) {
    const {
        requests: { requests, getting, errors },
        auth: { user },
    } = state;
    return { requests, getting, errors, user };
}

const actionsToProps = {
    clearErrors: requestsActions.clearErrors,
    saveExtData: requestsActions.saveExtData,
};

export default connect(mapStateToProps, actionsToProps)(ExtDataPage);
